
import {
  computed, defineComponent, inject, onMounted, onUnmounted, ref, watch,
} from 'vue';
import RefreshPull from 'pull-refresh-vue3';
import { useRouter } from 'vue-router';
import {
  APPLICATION_SETTINGS_TYPE,
  CONTACT_STATUS,
  NETWORK_CONTACT_TYPES,
  NOTIFICATION_READ_MODE,
  NOTIFICATION_TYPE,
} from '@/types/enums';
// eslint-disable-next-line import/no-cycle
import { useSpacesStore } from '../store/spacesStore';
import SpacesInvitations from '../components/SpacesInvitations.vue';
import SpacesSuggestions from '../components/SpacesSuggestions.vue';
import SpacesOutcoming from '../components/SpacesOutcoming.vue';
import SpacesMy from '../components/SpacesMy.vue';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
// eslint-disable-next-line import/no-cycle
import NativeService from '@/services/NativeService';
// eslint-disable-next-line import/no-cycle
import UserService from '@/services/UserService';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import SpacesUser from '../components/SpacesUser.vue';
import AppItemWrapper from '@/components/AppItemWrapper.vue';
import SpacesMyGroups from '@/modules/spaces/components/SpacesMyGroups.vue';
import { INotificationRead } from '@/types/interfaces';
import AppNoMessage from '@/components/AppNoMessage.vue';

export default defineComponent({
  components: {
    AppNoMessage,
    SpacesInvitations,
    // SpacesSuggestions,
    SpacesOutcoming,
    SpacesMy,
    RefreshPull,
    SpacesUser,
    AppItemWrapper,
  },
  setup() {
    const spacesStore = useSpacesStore();
    const mainStore = useMainStore();

    const hasContacts = computed(() => spacesStore.myContacts.length > 0);
    const noSearchResult = computed(() => (
      !hasContacts.value && spacesStore.search.length) || (hasContacts.value && spacesStore.search.length && !spacesStore.filteredMyContacts.length));
    const noData = computed(
      () => !spacesStore.filteredOutgoingContact.length
        && !spacesStore.filteredMyContacts.length
        && !spacesStore.filteredInvitationsContact.length
        && !spacesStore.filteredSuggestions.length,
    );

    const isManageContacts = computed(
      () => mainStore.applicationSettings.find((feature) => feature.name === APPLICATION_SETTINGS_TYPE.CONTACT_NETWORKING_FEATURE)?.enabled,
    );
    const myGroups = computed(() => spacesStore.myGroups.length);
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;
    function shareApp(): void {
      if (mainStore.isNative) {
        NativeService.shareApp();
      }
    }

    const noDataMessage = computed(() => {
      if (noSearchResult.value) {
        return {
          text: $localize('no.search.results'),
          icon: 'icon-not-found',
        };
      }
      return '';
    });

    function refreshContacts() {
      return Promise.all([spacesStore.fetchInvitationsContact(),
        spacesStore.fetchSuggestions(),
        spacesStore.fetchMyContacts(),
        spacesStore.fetchOutgoingContact(),
        spacesStore.fetchMyGroups(),
        spacesStore.fetchUsersGroups()]);
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshContacts);

    // search logic
    const scroller = ref<HTMLElement | null>(null);

    let pagesAmount = 0;
    let currentPageIndex = 0;

    function searchContacts(query: string, pageIndex: number, pageSize: number, push?: boolean): void {
      spacesStore.searchContacts(query, pageIndex, pageSize, push).then((response) => {
        if (response.success) {
          pagesAmount = response.results.data.totalPages;
          currentPageIndex = response.results.data.number;
        }
      });
    }

    function onScroll() {
      const isBottom = Math.round(scroller.value!.scrollTop + scroller.value!.clientHeight) >= Math.round(scroller.value!.scrollHeight);

      if (isBottom && pagesAmount !== currentPageIndex + 1) {
        searchContacts(spacesStore.search, currentPageIndex + 1, 10, true);
      }
    }

    function resolveUserType(contactStatus: CONTACT_STATUS, accountId: string): NETWORK_CONTACT_TYPES {
      if (contactStatus === CONTACT_STATUS.REQUEST_SENT) {
        return NETWORK_CONTACT_TYPES.OUTCOMING;
      }
      if (contactStatus === CONTACT_STATUS.REQUEST_RECEIVED) {
        return NETWORK_CONTACT_TYPES.INVITATION;
      }
      if (contactStatus === CONTACT_STATUS.CONTACT) {
        return NETWORK_CONTACT_TYPES.MY;
      }
      if (accountId === mainStore.user.accountId) {
        return NETWORK_CONTACT_TYPES.ME;
      }
      return NETWORK_CONTACT_TYPES.SUGGESTION;
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => spacesStore.search, (newSearch, oldSearch) => {
      spacesStore.dataLoading = true;
      clearTimeout(timeout);
      if (newSearch === '') {
        spacesStore.searchContactsList = [];
      } else {
        timeout = setTimeout(() => {
          searchContacts(newSearch, 0, 10);
        }, 300);
      }
    });
    // watch(() => driveStore.search, (newSearch, oldSearch) => {
    //   clearTimeout(timeout);
    //   pageIdx.value = 0;
    //   dataLoading.value = !!newSearch;
    //   driveStore.dataLoading.mixed = true;
    //   timeout = setTimeout(() => {
    //     fetchSearchData()
    //       .then((response) => {
    //         setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
    //       })
    //       .finally(() => {
    //         dataLoading.value = false;
    //         driveStore.dataLoading.mixed = false;
    //       });
    //   }, 1500);
    // });

    const myContactsSearch = computed(() => {
      const arr = [...spacesStore.searchContactsList.filter((cont) => resolveUserType(cont.contactStatus, cont.accountId) === NETWORK_CONTACT_TYPES.MY)];
      const me = spacesStore.searchContactsList.find((cont) => resolveUserType(cont.contactStatus, cont.accountId) === NETWORK_CONTACT_TYPES.ME);
      if (me) {
        arr.unshift(me);
      }
      return arr;
    });

    const notMyContactsSearch = computed(() => {
      const arr = [...spacesStore.searchContactsList.filter((cont) => {
        const type = resolveUserType(cont.contactStatus, cont.accountId);
        return type !== NETWORK_CONTACT_TYPES.MY && type !== NETWORK_CONTACT_TYPES.ME;
      })];
      return arr;
    });

    function readNotification() {
      if (mainStore.badgesContact) {
        const payload: INotificationRead[] = [{
          type: NOTIFICATION_TYPE.CONTACT_REQUEST_SENT,
          mode: NOTIFICATION_READ_MODE.BY_TYPE,
        }];
        UserService.readNotification(payload);
        mainStore.badgesAlert -= mainStore.badgesContact;
        mainStore.badgesContact = 0;
      }
    }

    watch(() => spacesStore.filteredInvitationsContact.length, () => {
      readNotification();
    });

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      readNotification();
    });

    onUnmounted(() => {
      spacesStore.search = '';
    });

    return {
      hasContacts,
      isManageContacts,
      noData,
      spacesStore,
      NETWORK_CONTACT_TYPES,
      noSearchResult,
      shareApp,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,

      scroller,
      resolveUserType,
      onScroll,
      mainStore,
      myContactsSearch,
      notMyContactsSearch,
      myGroups,
      noDataMessage,
    };
  },
});
